import React, { useEffect, useMemo, useRef, useState } from "react";
import { CtyButton } from "../button/CtyButton";
import { SearchableDropdown } from "./searchabledropdown";
import { SpeechUtility } from "../utils/speechUtils";
import { callLLMAction, CALL_LLM_DEFAULT_MODEL, type AppDispatch } from "@circuitry-ai/doc-data";

export interface VoiceFormAssistantPayload {
  fieldId?: string;
  fieldName?: string;
  label: string;
  value: string;
}

export interface VoiceFormFieldMeta {
  fieldId?: string;
  fieldName?: string;
  label: string;
  type: string;
  sectionTitle?: string;
  options?: { label: string; value: string }[];
  placeholder?: string;
}

export interface VoiceFormAssistantProps {
  enabled: boolean;
  containerRef?: React.RefObject<HTMLElement>;
  selector?: string;
  disabled?: boolean;
  dispatch: AppDispatch;
  speechSilenceMs?: number;
  speechStartTimeoutMs?: number;
  narrationMode?: boolean;
  narrationWelcomeText?: string;
  narrationSkipWelcome?: boolean;
  parseTranscript?: (payload: {
    transcript: string;
    fields: VoiceFormFieldMeta[];
  }) => Promise<VoiceFormAssistantPayload[]>;
  modelName?: string;
  maxTokens?: number;
  onToggle?: (nextEnabled: boolean) => void;
  toggleLabel?: {
    on?: string;
    off?: string;
  };
  showLanguageSelector?: boolean;
  showControls?: boolean;
  showToggleButton?: boolean;
  showNarrationOverlay?: boolean;
  continuousNarration?: boolean;
  controlsClassName?: string;
  className?: string;
  statusText?: {
    idle?: string;
    listening?: string;
    processing?: string;
  };
  labelResolver?: (field: HTMLElement) => string;
  shouldSkipField?: (field: HTMLElement) => boolean;
  onFieldCaptured?: (payload: VoiceFormAssistantPayload) => void;
  onNarrationPartial?: (partial: string) => void;
  onNarrationComplete?: (transcript: string) => void;
  onExit?: () => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
  onLanguageChange?: (language: string, label?: string) => void;
}

const defaultSelector = "input, textarea, select";
const activeHighlightClass = "voice-form-assistant-active";
const DEFAULT_MODEL = CALL_LLM_DEFAULT_MODEL;
const DEFAULT_MAX_TOKENS = 8096;

const isHiddenInput = (field: HTMLInputElement) =>
  field.type === "hidden" || field.type === "submit" || field.type === "reset";

const isActionInput = (field: HTMLInputElement) =>
  field.type === "button" || field.type === "image" || field.type === "file";

const isTextLikeInput = (field: HTMLInputElement) => {
  const type = field.type?.toLowerCase();
  return (
    !type ||
    [
      "text",
      "email",
      "number",
      "search",
      "tel",
      "url",
      "password",
      "date",
      "datetime-local",
      "month",
      "time",
      "week",
    ].includes(type)
  );
};

const normalizeValue = (value: string) => value.trim();

const normalizeSelectToken = (value: string) => {
  const trimmed = value.trim().toLowerCase();
  if (trimmed.endsWith(".") || trimmed.endsWith("?") || trimmed.endsWith("!")) {
    return trimmed.slice(0, -1);
  }
  return trimmed;
};
const isAffirmative = (value: string) =>
  ["yes", "true", "on", "checked", "check", "select", "enable", "enabled", "ok"].includes(
    value.toLowerCase()
  );

const isNegative = (value: string) =>
  ["no", "false", "off", "unchecked", "uncheck", "deselect", "disable", "disabled"].includes(
    value.toLowerCase()
  );

const getLabelFromField = (field: HTMLElement) => {
  const ariaLabel = field.getAttribute("aria-label");
  if (ariaLabel) return ariaLabel;
  const dataLabel = field.getAttribute("data-label");
  if (dataLabel) return dataLabel;
  const labelledBy = field.getAttribute("aria-labelledby");
  if (labelledBy) {
    const labelText = labelledBy
      .split(" ")
      .map((id) => field.ownerDocument?.getElementById(id)?.textContent?.trim())
      .filter(Boolean)
      .join(" ");
    if (labelText) return labelText;
  }
  const id = field.getAttribute("id");
  if (id) {
    const label = field.ownerDocument?.querySelector(`label[for="${id}"]`);
    if (label?.textContent) return label.textContent.trim();
    const labelById = field.ownerDocument?.getElementById(`${id}-label`);
    if (labelById?.textContent) return labelById.textContent.trim();
    const suffixes = ["-input", "-select", "-textarea"];
    const matchedSuffix = suffixes.find((suffix) => id.endsWith(suffix));
    if (matchedSuffix) {
      const guessedId = `${id.slice(0, -matchedSuffix.length)}-label`;
      const guessedLabel = field.ownerDocument?.getElementById(guessedId);
      if (guessedLabel?.textContent) return guessedLabel.textContent.trim();
    }
  }
  const wrapperLabel = field.closest("label");
  if (wrapperLabel?.textContent) return wrapperLabel.textContent.trim();
  const parent = field.parentElement;
  if (parent) {
    const labelCandidate = parent.querySelector(
      "label"
    ) as HTMLElement | null;
    if (labelCandidate?.textContent) return labelCandidate.textContent.trim();
    const prevSibling = field.previousElementSibling as HTMLElement | null;
    if (prevSibling?.textContent) return prevSibling.textContent.trim();
    const parentPrevSibling = parent.previousElementSibling as HTMLElement | null;
    if (parentPrevSibling?.textContent) return parentPrevSibling.textContent.trim();
  }
  const placeholder = (field as HTMLInputElement).placeholder;
  if (placeholder) return placeholder;
  const name = field.getAttribute("name");
  if (name) return name;
  return "Input";
};

const trimTrailingPeriodForShortText = (value: string, maxLength: number = 80) => {
  const trimmed = value.trim();
  if (trimmed.length > 0 && trimmed.length <= maxLength && trimmed.endsWith(".")) {
    return trimmed.slice(0, -1);
  }
  return trimmed;
};

const extractJsonArray = (text: string) => {
  const fenceStart = text.toLowerCase().indexOf("```json");
  if (fenceStart !== -1) {
    const afterStart = text.indexOf("\n", fenceStart);
    const contentStart = afterStart !== -1 ? afterStart + 1 : fenceStart + 7;
    const fenceEnd = text.indexOf("```", contentStart);
    if (fenceEnd !== -1) {
      return text.slice(contentStart, fenceEnd).trim();
    }
  }
  const start = text.indexOf("[");
  const end = text.lastIndexOf("]");
  if (start !== -1 && end !== -1 && end > start) {
    return text.slice(start, end + 1);
  }
  return text.trim();
};

const getFieldKey = (field: HTMLElement, fallbackIndex?: number) => {
  const existing = field.getAttribute("data-voice-key");
  if (existing) return existing;
  const key = field.getAttribute("id") ?? field.getAttribute("name") ?? undefined;
  if (key) {
    field.setAttribute("data-voice-key", key);
    return key;
  }
  if (typeof fallbackIndex === "number") {
    const generated = `field-${fallbackIndex}`;
    field.setAttribute("data-voice-key", generated);
    return generated;
  }
  return undefined;
};

const getSectionTitle = (field: HTMLElement) => {
  let current: HTMLElement | null = field.parentElement;
  while (current) {
    const explicit = current.querySelector("[data-section-title]") as HTMLElement | null;
    if (explicit?.textContent?.trim()) return explicit.textContent.trim();
    const heading = current.querySelector("legend, h1, h2, h3, h4, h5, h6") as HTMLElement | null;
    if (heading?.textContent?.trim()) return heading.textContent.trim();
    const styled = current.querySelector(".tracking-wide.uppercase, .uppercase.font-bold") as HTMLElement | null;
    if (styled?.textContent?.trim()) return styled.textContent.trim();
    current = current.parentElement;
  }
  return "";
};

const getCheckboxGroupContainer = (field: HTMLInputElement) => {
  const labelWrapper = field.closest("label");
  if (labelWrapper) return labelWrapper.parentElement as HTMLElement | null;
  const section =
    field.closest("[data-section-title]") ??
    field.closest("fieldset") ??
    field.closest("[data-voice-scope]");
  if (section) return section as HTMLElement;
  return field.parentElement;
};

const getCheckboxGroupFields = (field: HTMLInputElement) => {
  const container = getCheckboxGroupContainer(field);
  if (!container) return [field];
  return Array.from(container.querySelectorAll('input[type="checkbox"]')) as HTMLInputElement[];
};

const getCheckboxGroupLabel = (field: HTMLInputElement) => {
  const container = getCheckboxGroupContainer(field);
  if (!container) return "Checkbox options";
  const labelEl = container.querySelector(
    "legend, h1, h2, h3, h4, h5, h6, .font-semibold, strong, b"
  ) as HTMLElement | null;
  const labelText = labelEl?.textContent?.trim();
  return labelText || "Checkbox options";
};

const isFocusableField = (field: HTMLElement) => {
  if ((field as HTMLInputElement).disabled) return false;
  const tabIndex = field.getAttribute("tabindex");
  if (tabIndex !== null && Number(tabIndex) < 0) return false;
  return true;
};

const getFieldType = (field: HTMLElement) => {
  if (field instanceof HTMLInputElement) {
    return field.type?.toLowerCase() || "text";
  }
  if (field instanceof HTMLTextAreaElement) return "textarea";
  if (field instanceof HTMLSelectElement) return "select";
  return "unknown";
};

const getSelectOptions = (field: HTMLSelectElement) =>
  Array.from(field.options).map((option) => ({
    label: option.label,
    value: option.value,
  }));

const isFieldMissing = (field: HTMLElement) => {
  if (field.getAttribute("data-voice-filled") === "true") return false;
  if (field instanceof HTMLInputElement) {
    const type = field.type?.toLowerCase();
    if (type === "checkbox") {
      const group = getCheckboxGroupFields(field);
      return !group.some((item) => item.checked);
    }
    if (type === "radio") return false;
    if (isTextLikeInput(field)) return !field.value?.trim();
    return false;
  }
  if (field instanceof HTMLTextAreaElement) return !field.value?.trim();
  if (field instanceof HTMLSelectElement) return true;
  return false;
};

const setNativeValue = (element: HTMLInputElement | HTMLTextAreaElement, value: string) => {
  const prototype = Object.getPrototypeOf(element);
  const valueSetter = Object.getOwnPropertyDescriptor(prototype, "value")?.set;
  if (valueSetter) {
    valueSetter.call(element, value);
  } else {
    element.value = value;
  }
  element.dispatchEvent(new Event("input", { bubbles: true }));
  element.dispatchEvent(new Event("change", { bubbles: true }));
};

const setCheckboxValue = (field: HTMLInputElement, value: string) => {
  const normalized = normalizeValue(value);
  if (!normalized) return;
  const groupFields = getCheckboxGroupFields(field);
  const normalizedLower = normalized.toLowerCase();
  const matchedOptions = groupFields.filter((option) =>
    normalizedLower.includes(getLabelFromField(option).toLowerCase())
  );
  if (matchedOptions.length > 0) {
    matchedOptions.forEach((option) => {
      const prototype = Object.getPrototypeOf(option);
      const checkedSetter = Object.getOwnPropertyDescriptor(prototype, "checked")?.set;
      if (checkedSetter) {
        checkedSetter.call(option, true);
      } else {
        option.checked = true;
      }
      option.dispatchEvent(new Event("input", { bubbles: true }));
      option.dispatchEvent(new Event("change", { bubbles: true }));
    });
    return;
  }
  const shouldCheck = isAffirmative(normalized)
    ? true
    : isNegative(normalized)
      ? false
      : field.checked;
  const prototype = Object.getPrototypeOf(field);
  const checkedSetter = Object.getOwnPropertyDescriptor(prototype, "checked")?.set;
  if (checkedSetter) {
    checkedSetter.call(field, shouldCheck);
  } else {
    field.checked = shouldCheck;
  }
  field.dispatchEvent(new Event("input", { bubbles: true }));
  field.dispatchEvent(new Event("change", { bubbles: true }));
};

const setRadioValue = (field: HTMLInputElement, value: string) => {
  const form = field.form ?? field.ownerDocument;
  const name = field.name;
  if (!name) return;
  const radios = Array.from(form.querySelectorAll(`input[type="radio"][name="${name}"]`));
  const match = radios.find((radio) => {
    const label = getLabelFromField(radio as HTMLElement);
    return label.toLowerCase() === value.toLowerCase();
  });
  const target = (match ?? field) as HTMLInputElement;
  target.checked = true;
  target.dispatchEvent(new Event("change", { bubbles: true }));
};

const setSelectValue = (field: HTMLSelectElement, value: string) => {
  const normalized = normalizeSelectToken(value);
  const options = Array.from(field.options);
  const option =
    options.find(
      (item) =>
        normalizeSelectToken(item.label) === normalized ||
        normalizeSelectToken(item.value) === normalized
    ) ??
    options.find(
      (item) =>
        normalizeSelectToken(item.label).includes(normalized) ||
        normalizeSelectToken(item.value).includes(normalized)
    );
  if (option) {
    field.value = option.value;
  } else {
    field.value = value;
  }
  field.dispatchEvent(new Event("change", { bubbles: true }));
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (error && typeof error === "object") {
    const errorDetails = (error as { errorDetails?: string }).errorDetails;
    const message = (error as { message?: string }).message;
    if (errorDetails) return errorDetails;
    if (message) return message;
  }
  return "Speech service failed.";
};

const VoiceFormAssistant = ({
  enabled,
  containerRef,
  selector = defaultSelector,
  disabled = false,
  dispatch,
  speechSilenceMs = 2000,
  speechStartTimeoutMs = 5000,
  narrationMode = false,
  narrationWelcomeText,
  narrationSkipWelcome = false,
  parseTranscript,
  modelName = DEFAULT_MODEL,
  maxTokens = DEFAULT_MAX_TOKENS,
  onToggle,
  toggleLabel,
  showLanguageSelector = true,
  showControls = true,
  showToggleButton = true,
  showNarrationOverlay = true,
  continuousNarration = false,
  controlsClassName = "",
  className,
  statusText,
  labelResolver,
  shouldSkipField,
  onFieldCaptured,
  onNarrationPartial,
  onNarrationComplete,
  onExit,
  onComplete,
  onError,
  onLanguageChange,
}: VoiceFormAssistantProps) => {
  const [isRunning, setIsRunning] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isNarrationFlow, setIsNarrationFlow] = useState(false);
  const [narrationStage, setNarrationStage] = useState<
    "idle" | "welcome" | "listening" | "analyzing" | "filling" | "interactive"
  >("idle");
  const [narrationTranscript, setNarrationTranscript] = useState("");
  const [narrationPartial, setNarrationPartial] = useState("");
  const [interactivePartial, setInteractivePartial] = useState("");
  const [interactiveOverlayPos, setInteractiveOverlayPos] = useState<{
    top: number;
    left: number;
    placement: "above" | "below";
  } | null>(null);
  const [isInteractiveListening, setIsInteractiveListening] = useState(false);
  const [interactiveElapsedMs, setInteractiveElapsedMs] = useState(0);
  const [interactiveRemainingMs, setInteractiveRemainingMs] = useState(0);
  const [isSystemSpeaking, setIsSystemSpeaking] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    SpeechUtility.getLanguage() ?? "en-US"
  );
  const onLanguageChangeRef = useRef(onLanguageChange);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const activeFieldRef = useRef<HTMLElement | null>(null);
  const activeHighlightRef = useRef<HTMLElement | null>(null);
  const pendingStartFieldRef = useRef<HTMLElement | null>(null);
  const missingFieldsRef = useRef<HTMLElement[] | null>(null);
  const spokenLabelMapRef = useRef<Map<string, string>>(new Map());
  const checkboxGroupKeyRef = useRef<Map<HTMLElement, string>>(new Map());
  const autoFocusRef = useRef(false);
  const askedFieldKeysRef = useRef<Set<string>>(new Set());
  const lastTranscriptRef = useRef<string>("");
  const cancelRef = useRef(false);
  const runIdRef = useRef(0);
  const previousStyleRef = useRef<{
    outline?: string;
    boxShadow?: string;
    borderColor?: string;
    borderImage?: string;
    borderImageSlice?: string;
    borderStyle?: string;
    borderWidth?: string;
    position?: string;
    overflow?: string;
  } | null>(null);

  const getFields = useMemo(() => {
    return () => {
      const root = containerRef?.current ?? document;
      const fields = Array.from(root.querySelectorAll(selector)).filter((node) => {
        if (!(node instanceof HTMLElement)) return false;
        if (shouldSkipField?.(node)) return false;
        if ((node as HTMLInputElement).disabled) return false;
        const fieldset = node.closest("fieldset");
        if ((fieldset as HTMLFieldSetElement | null)?.disabled) return false;
        if (node instanceof HTMLInputElement) {
          if (isHiddenInput(node) || isActionInput(node)) return false;
        }
        return true;
      });
      return fields;
    };
  }, [containerRef, selector, shouldSkipField]);

  const clearHighlight = () => {
    if (!activeHighlightRef.current) return;
    const element = activeHighlightRef.current;
    if (previousStyleRef.current) {
      element.style.outline = previousStyleRef.current.outline ?? "";
      element.style.boxShadow = previousStyleRef.current.boxShadow ?? "";
      element.style.borderColor = previousStyleRef.current.borderColor ?? "";
      element.style.borderImage = previousStyleRef.current.borderImage ?? "";
      element.style.borderImageSlice = previousStyleRef.current.borderImageSlice ?? "";
      element.style.borderStyle = previousStyleRef.current.borderStyle ?? "";
      element.style.borderWidth = previousStyleRef.current.borderWidth ?? "";
      element.style.position = previousStyleRef.current.position ?? "";
      element.style.overflow = previousStyleRef.current.overflow ?? "";
    }
    activeFieldRef.current = null;
    activeHighlightRef.current = null;
    previousStyleRef.current = null;
  };

  const getHighlightTarget = (field: HTMLElement) => {
    if (field instanceof HTMLInputElement) {
      const type = field.type?.toLowerCase();
      if (type === "checkbox" || type === "radio") {
        const labelWrapper = field.closest("label") as HTMLElement | null;
        if (labelWrapper) return labelWrapper;
        const parentLabel = field.parentElement?.querySelector("label") as HTMLElement | null;
        if (parentLabel) return parentLabel;
      }
    }
    const wrapper = field.closest(
      "[data-slot='input-wrapper'], [data-slot='base'], [data-slot='wrapper']"
    ) as HTMLElement | null;
    return wrapper ?? field;
  };

  const highlightField = (field: HTMLElement) => {
    if (activeFieldRef.current === field) return;
    clearHighlight();
    const target = getHighlightTarget(field);
    previousStyleRef.current = {
      outline: target.style.outline,
      boxShadow: target.style.boxShadow,
      borderColor: target.style.borderColor,
      borderImage: target.style.borderImage,
      borderImageSlice: target.style.borderImageSlice,
      borderStyle: target.style.borderStyle,
      borderWidth: target.style.borderWidth,
      position: target.style.position,
      overflow: target.style.overflow,
    };
    target.style.outline = "2px solid transparent";
    target.style.boxShadow = "";
    target.style.borderStyle = "solid";
    target.style.borderWidth = "2px";
    target.style.borderImage = "linear-gradient(to right, #D4007E, #003A79)";
    target.style.borderImageSlice = "1";
    activeFieldRef.current = field;
    activeHighlightRef.current = target;
  };

  const updateInteractiveOverlayPosition = () => {
    if (!activeHighlightRef.current) return;
    const rect = activeHighlightRef.current.getBoundingClientRect();
    const overlayWidth = 280;
    const overlayHeight = 120;
    const left = rect.left + window.scrollX;
    const aboveTop = rect.top + window.scrollY - 8;
    const belowTop = rect.bottom + window.scrollY + 8;
    const hasRoomBelow = rect.bottom + overlayHeight + 8 < window.innerHeight;
    const hasRoomAbove = rect.top - overlayHeight - 8 > 0;
    const placement = hasRoomBelow ? "below" : hasRoomAbove ? "above" : "left";
    const leftPosition =
      placement === "left"
        ? rect.left + window.scrollX - overlayWidth - 8
        : left;
    setInteractiveOverlayPos({
      top: placement === "above" ? aboveTop : belowTop,
      left: leftPosition,
      placement,
    });
  };

const applyValueToField = (field: HTMLElement, value: string) => {
  const normalized = normalizeValue(value);
  if (field instanceof HTMLInputElement) {
    if (field.type === "checkbox") {
      setCheckboxValue(field, normalized);
      field.setAttribute("data-voice-filled", "true");
    } else if (field.type === "radio") {
      setRadioValue(field, normalized);
      field.setAttribute("data-voice-filled", "true");
    } else if (isTextLikeInput(field)) {
      setNativeValue(field, normalized);
      field.setAttribute("data-voice-filled", "true");
    }
  } else if (field instanceof HTMLTextAreaElement) {
    setNativeValue(field, normalized);
    field.setAttribute("data-voice-filled", "true");
  } else if (field instanceof HTMLSelectElement) {
    setSelectValue(field, normalized);
    field.setAttribute("data-voice-filled", "true");
  }
};

  const collectFieldMeta = (field: HTMLElement): VoiceFormFieldMeta => {
    let label = labelResolver ? labelResolver(field) : getLabelFromField(field);
    const type = getFieldType(field);
    let options: { label: string; value: string }[] | undefined;
    const sectionTitle = getSectionTitle(field) || undefined;
    if (field instanceof HTMLSelectElement) {
      options = getSelectOptions(field);
    }
    if (field instanceof HTMLInputElement && field.type?.toLowerCase() === "checkbox") {
      const groupLabel = getCheckboxGroupLabel(field);
      const groupOptions = getCheckboxGroupFields(field).map((option) => ({
        label: getLabelFromField(option),
        value: option.value,
      }));
      label = groupLabel;
      options = groupOptions;
    }
    const placeholder =
      field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement
        ? field.placeholder
        : undefined;
    return {
      fieldId: field.getAttribute("id") ?? undefined,
      fieldName: field.getAttribute("name") ?? undefined,
      label,
      type,
      sectionTitle,
      options,
      placeholder,
    };
  };

  const findFieldForPayload = (fields: HTMLElement[], payload: VoiceFormAssistantPayload) => {
    if (payload.fieldId) {
      const byId = fields.find((field) => field.getAttribute("id") === payload.fieldId);
      if (byId) return byId;
    }
    if (payload.fieldName) {
      const byName = fields.find((field) => field.getAttribute("name") === payload.fieldName);
      if (byName) return byName;
    }
    const normalizedLabel = payload.label.trim().toLowerCase();
    const directMatch = fields.find((field) => {
      const label = labelResolver ? labelResolver(field) : getLabelFromField(field);
      return label.trim().toLowerCase() === normalizedLabel;
    });
    if (directMatch) return directMatch;
    const checkboxField = fields.find((field) => {
      if (!(field instanceof HTMLInputElement)) return false;
      if (field.type?.toLowerCase() !== "checkbox") return false;
      const groupLabel = getCheckboxGroupLabel(field);
      return groupLabel.trim().toLowerCase() === normalizedLabel;
    });
    return checkboxField;
  };

  const captureField = async (field: HTMLElement, runId: number) => {
    let label = labelResolver ? labelResolver(field) : getLabelFromField(field);
    const fieldKey = getFieldKey(field);
    if (fieldKey && spokenLabelMapRef.current.has(fieldKey)) {
      label = spokenLabelMapRef.current.get(fieldKey) ?? label;
    }
    let checkboxOptions: string[] = [];
    let selectOptions: string[] = [];
    if (field instanceof HTMLInputElement && field.type?.toLowerCase() === "checkbox") {
      const groupLabel = getCheckboxGroupLabel(field);
      const groupKey = checkboxGroupKeyRef.current.get(getCheckboxGroupContainer(field) ?? field);
      const baseLabel =
        (groupKey && spokenLabelMapRef.current.get(groupKey)) || groupLabel || label;
      checkboxOptions = getCheckboxGroupFields(field)
        .map((option) => getLabelFromField(option))
        .filter(Boolean);
      label = baseLabel;
    }
    if (field instanceof HTMLSelectElement) {
      selectOptions = getSelectOptions(field).map((option) => option.label).filter(Boolean);
    }
    autoFocusRef.current = true;
    field.focus();
    highlightField(field);
    updateInteractiveOverlayPosition();
    try {
      const speechUtilityInstance = await SpeechUtility.getInstance(dispatch);
      if (cancelRef.current || runIdRef.current !== runId) return;
      let lastPartial = "";
      setInteractivePartial("");
      SpeechUtility.stopRecognition();
      const ttsPromise = new Promise<void>((resolve) => {
        let hasStarted = false;
        let isResolved = false;
        const resolveOnce = () => {
          if (isResolved) return;
          isResolved = true;
          clearTimeout(startTimeoutId);
          clearTimeout(maxTimeoutId);
          resolve();
        };
        const startTimeoutId = setTimeout(() => {
          if (!hasStarted) resolveOnce();
        }, 1500);
        const maxTimeoutId = setTimeout(() => {
          resolveOnce();
        }, 8000);
        speechUtilityInstance.textToSpeech(label, (isReading) => {
          if (isReading) {
            hasStarted = true;
            setIsSystemSpeaking(true);
            return;
          }
          if (hasStarted) {
            setIsSystemSpeaking(false);
            resolveOnce();
          }
        });
      });
      await ttsPromise;
      if (cancelRef.current || runIdRef.current !== runId) return;
      setIsProcessing(true);
      setIsInteractiveListening(true);
      setInteractiveElapsedMs(0);
      setInteractiveRemainingMs(0);
      const startedAt = Date.now();
      let lastSpeechAt: number | null = null;
      const intervalId = window.setInterval(() => {
        const now = Date.now();
        setInteractiveElapsedMs(now - startedAt);
        if (lastSpeechAt !== null) {
          const remaining = Math.max(0, speechSilenceMs - (now - lastSpeechAt));
          setInteractiveRemainingMs(remaining);
        }
      }, 200);
      const transcript = await speechUtilityInstance.recognizeOnceWithSilence(
        speechSilenceMs,
        speechStartTimeoutMs,
        (partial) => {
          if (partial) {
            lastSpeechAt = Date.now();
            lastPartial = partial;
            updateInteractiveOverlayPosition();
            setInteractivePartial(partial);
          }
        }
      );
      const maxListenMs = speechStartTimeoutMs + speechSilenceMs + 2000;
      const transcriptOrTimeout = await Promise.race([
        Promise.resolve(transcript),
        new Promise<string>((resolve) => {
          window.setTimeout(() => resolve(lastPartial), maxListenMs);
        }),
      ]);
      window.clearInterval(intervalId);
      setIsProcessing(false);
      setIsInteractiveListening(false);
      setInteractivePartial("");
      setInteractiveOverlayPos(null);
      setInteractiveElapsedMs(0);
      setInteractiveRemainingMs(0);
      if (cancelRef.current || runIdRef.current !== runId) return;
      if (transcriptOrTimeout) {
        lastTranscriptRef.current = transcriptOrTimeout;
        const translatedText =
          SpeechUtility.getLanguage() !== "en-US"
            ? await speechUtilityInstance.translateText(transcriptOrTimeout, "en")
            : transcriptOrTimeout;
        const cleanedText = trimTrailingPeriodForShortText(translatedText);
        applyValueToField(field, cleanedText);
        onFieldCaptured?.({
          fieldId: field.getAttribute("id") ?? undefined,
          fieldName: field.getAttribute("name") ?? undefined,
          label,
          value: cleanedText,
        });
      } else if (checkboxOptions.length || selectOptions.length) {
        const optionsToRead = checkboxOptions.length ? checkboxOptions : selectOptions;
        await new Promise<void>((resolve) => {
          let hasStarted = false;
          let isResolved = false;
          const resolveOnce = () => {
            if (isResolved) return;
            isResolved = true;
            resolve();
          };
          speechUtilityInstance.textToSpeech(
            `Options are: ${optionsToRead.join(", ")}.`,
            (isReading) => {
              if (isReading) {
                hasStarted = true;
                return;
              }
              if (hasStarted) resolveOnce();
            }
          );
        });

        setIsProcessing(true);
        setIsInteractiveListening(true);
        setInteractiveElapsedMs(0);
        setInteractiveRemainingMs(0);
        updateInteractiveOverlayPosition();
        const startedAt = Date.now();
        let lastSpeechAt: number | null = null;
        const intervalId = window.setInterval(() => {
          const now = Date.now();
          setInteractiveElapsedMs(now - startedAt);
          if (lastSpeechAt !== null) {
            const remaining = Math.max(0, speechSilenceMs - (now - lastSpeechAt));
            setInteractiveRemainingMs(remaining);
          }
        }, 200);

        const optionsTranscript = await speechUtilityInstance.recognizeOnceWithSilence(
          speechSilenceMs,
          speechStartTimeoutMs,
          (partial) => {
            if (partial) {
              lastSpeechAt = Date.now();
              updateInteractiveOverlayPosition();
              setInteractivePartial(partial);
            }
          }
        );

        const maxListenMs = speechStartTimeoutMs + speechSilenceMs + 2000;
        const optionsTranscriptOrTimeout = await Promise.race([
          Promise.resolve(optionsTranscript),
          new Promise<string>((resolve) => {
            window.setTimeout(() => resolve(lastPartial), maxListenMs);
          }),
        ]);

        window.clearInterval(intervalId);
        setIsProcessing(false);
        setIsInteractiveListening(false);
        setInteractivePartial("");
        setInteractiveOverlayPos(null);
        setInteractiveElapsedMs(0);
        setInteractiveRemainingMs(0);

        if (optionsTranscriptOrTimeout) {
          const translatedText =
            SpeechUtility.getLanguage() !== "en-US"
              ? await speechUtilityInstance.translateText(optionsTranscriptOrTimeout, "en")
              : optionsTranscriptOrTimeout;
          const cleanedText = trimTrailingPeriodForShortText(translatedText);
          applyValueToField(field, cleanedText);
          onFieldCaptured?.({
            fieldId: field.getAttribute("id") ?? undefined,
            fieldName: field.getAttribute("name") ?? undefined,
            label,
            value: cleanedText,
          });
        }
      }
    } catch (error) {
      const nextError = new Error(getErrorMessage(error));
      setErrorMessage(nextError.message);
      onError?.(nextError);
      stopVoiceMode();
      onToggle?.(false);
    } finally {
      setIsProcessing(false);
      setIsInteractiveListening(false);
      setInteractivePartial("");
      setInteractiveOverlayPos(null);
      setInteractiveElapsedMs(0);
      setInteractiveRemainingMs(0);
      setIsSystemSpeaking(false);
    }
  };

  const callPortkey = async (systemPrompt: string, userPrompt: string) =>
    callLLMAction({
      dispatch,
      systemPrompt,
      userPrompt,
      model: modelName,
      maxCompletionTokens: maxTokens,
    });

  const defaultParseTranscript = async ({
    transcript,
    fields,
  }: {
    transcript: string;
    fields: VoiceFormFieldMeta[];
  }) => {
    const uniqueFields = Array.from(
      new Map(fields.map((field) => [field.label, field])).values()
    );
    const fieldsText = uniqueFields
      .map((field) => {
        const section = field.sectionTitle ? ` Section: ${field.sectionTitle}.` : "";
        const options = field.options?.length
          ? ` Options: ${field.options.map((o) => o.label).join(", ")}`
          : "";
        return `- ${field.label} (${field.type}).${section}${options}`;
      })
      .join("\n");

    const systemPrompt = `You extract form field values from a user narration transcript.
Return ONLY a JSON array of objects: [{"label":"...","value":"..."}].
Use the provided label text exactly. If unsure, omit the field.
For checkboxes/radios, use "yes" or "no". For selects, use one of the option labels.`;

    const userPrompt = `Transcript:
"""${transcript}"""

Fields:
${fieldsText}

Return JSON only.`;

    const response = await callPortkey(systemPrompt, userPrompt);
    const jsonText = extractJsonArray(response);
    const parsed = JSON.parse(jsonText);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item) => item && typeof item.label === "string" && typeof item.value === "string")
      .map((item) => ({ label: item.label, value: item.value }));
  };

  const ensureSpokenLabels = async (fields: HTMLElement[]) => {
    if (spokenLabelMapRef.current.size > 0) return;
    const items: Array<{
      key: string;
      label: string;
      type: string;
      sectionTitle?: string;
      options?: { label: string; value: string }[];
      placeholder?: string;
    }> = [];
    const seenGroupKeys = new Set<string>();
    fields.forEach((field, index) => {
      const key = getFieldKey(field, index);
      if (!key) return;
      if (field instanceof HTMLInputElement && field.type?.toLowerCase() === "checkbox") {
        const container = getCheckboxGroupContainer(field) ?? field;
        let groupKey = checkboxGroupKeyRef.current.get(container);
        if (!groupKey) {
          groupKey = `group-${key}`;
          checkboxGroupKeyRef.current.set(container, groupKey);
        }
        if (!seenGroupKeys.has(groupKey)) {
          const groupLabel = getCheckboxGroupLabel(field);
          const options = getCheckboxGroupFields(field).map((option) => ({
            label: getLabelFromField(option),
            value: option.value,
          }));
          items.push({
            key: groupKey,
            label: groupLabel,
            type: "checkbox-group",
            sectionTitle: getSectionTitle(field) || undefined,
            options,
          });
          seenGroupKeys.add(groupKey);
        }
        return;
      }
      items.push({
        key,
        label: labelResolver ? labelResolver(field) : getLabelFromField(field),
        type: getFieldType(field),
        sectionTitle: getSectionTitle(field) || undefined,
        options: field instanceof HTMLSelectElement ? getSelectOptions(field) : undefined,
        placeholder:
          field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement
            ? field.placeholder
            : undefined,
      });
    });

    if (!items.length) return;
    const lines = items
      .map((item) => {
        const section = item.sectionTitle ? ` Section: ${item.sectionTitle}.` : "";
        const options = item.options?.length
          ? ` Options: ${item.options.map((o) => o.label).join(", ")}`
          : "";
        const placeholder = item.placeholder ? ` Placeholder: ${item.placeholder}.` : "";
        return `- key: ${item.key}\n  label: ${item.label}\n  type: ${item.type}.${section}${options}${placeholder}`;
      })
      .join("\n");

    const systemPrompt = `You create natural, conversational questions for form fields.
Return ONLY JSON array of objects: [{"key":"...","spokenLabel":"..."}].
Use section context to make labels meaningful. Speak like a helpful person asking a question.
Avoid imperative phrasing like "Enter" or "Fill". Keep it concise.`;

    const userPrompt = `Fields:\n${lines}\n\nReturn JSON only.`;

    const response = await callPortkey(systemPrompt, userPrompt);
    const jsonText = extractJsonArray(response);
    const parsed = JSON.parse(jsonText);
    if (Array.isArray(parsed)) {
      parsed.forEach((item) => {
        if (item && typeof item.key === "string" && typeof item.spokenLabel === "string") {
          spokenLabelMapRef.current.set(item.key, item.spokenLabel);
        }
      });
    }
  };

  const runNarrationFlow = async () => {
    if (disabled) return;
    setIsRunning(true);
    setIsNarrationFlow(true);
    setErrorMessage(null);
    try {
      const speechUtilityInstance = await SpeechUtility.getInstance(dispatch);
      if (continuousNarration) {
        setNarrationStage("listening");
        setNarrationTranscript("");
        setNarrationPartial("");
        setIsProcessing(false);
        await speechUtilityInstance.startContinuousRecognition({
          translateToEnglish: selectedLanguage !== "en-US",
          onPartial: (partial) => {
            setNarrationPartial(partial);
            onNarrationPartial?.(partial);
          },
          onFinal: (finalText) => {
            void (async () => {
              const finalTranscript = finalText;
              setNarrationPartial("");
              onNarrationPartial?.("");
              setNarrationTranscript((prev) =>
                prev ? `${prev} ${finalTranscript}`.trim() : finalTranscript
              );
              if (finalTranscript) {
                onNarrationComplete?.(finalTranscript);
              }
              if (!finalTranscript) return;
              const fields = getFields();
              if (!fields.length) return;
              const fieldMeta = fields.map(collectFieldMeta);
              const parser = parseTranscript ?? defaultParseTranscript;
              try {
                const parsed = await parser({ transcript: finalTranscript, fields: fieldMeta });
                parsed.forEach((payload) => {
                  const target = findFieldForPayload(fields, payload);
                  if (target) {
                    applyValueToField(target, payload.value);
                  }
                });
              } catch {
                // Ignore parse errors in continuous mode
              }
            })();
          },
          onError: (err) => {
            setErrorMessage(err.message);
            onError?.(err);
            stopVoiceMode();
            onToggle?.(false);
          },
        });
        return;
      }
      if (!narrationSkipWelcome) {
        setNarrationStage("welcome");
        await new Promise<void>((resolve) => {
          let hasStarted = false;
          let isResolved = false;
          const resolveOnce = () => {
            if (isResolved) return;
            isResolved = true;
            resolve();
          };
          speechUtilityInstance.textToSpeech(
            narrationWelcomeText ??
              "Please describe the information to fill this form. I will listen and then ask for any missing details.",
            (isReading) => {
              if (isReading) {
                hasStarted = true;
                return;
              }
              if (hasStarted) resolveOnce();
            }
          );
        });
      }
      setNarrationStage("listening");
      setNarrationTranscript("");
      setNarrationPartial("");
      setIsProcessing(true);
      const transcript = await speechUtilityInstance.recognizeOnceWithSilence(
        speechSilenceMs,
        speechStartTimeoutMs,
        (partial) => {
          setNarrationPartial(partial);
          onNarrationPartial?.(partial);
        }
      );
      setIsProcessing(false);
      setNarrationPartial("");
      let finalTranscript = transcript;
      if (finalTranscript && selectedLanguage !== "en-US") {
        try {
          const translator = await SpeechUtility.getInstance(dispatch);
          finalTranscript = await translator.translateText(finalTranscript, "en");
        } catch {
          // Keep original transcript if translation fails
        }
      }
      setNarrationTranscript(finalTranscript);
      onNarrationPartial?.("");
      if (transcript) {
        onNarrationComplete?.(transcript);
      }
      if (!finalTranscript) {
        const fields = getFields();
        void ensureSpokenLabels(fields);
        const missingFields = fields.filter((field) => isFieldMissing(field));
        if (missingFields.length) {
          if (fields.length <= 1) {
            setNarrationStage("idle");
            setIsNarrationFlow(false);
            setIsRunning(false);
            onToggle?.(false);
            return;
          }
          const dedupedMissing: HTMLElement[] = [];
          const handledGroups = new Set<HTMLElement>();
          missingFields.forEach((field) => {
            if (field instanceof HTMLInputElement && field.type?.toLowerCase() === "checkbox") {
              const container = getCheckboxGroupContainer(field);
              if (container && handledGroups.has(container)) return;
              if (container) handledGroups.add(container);
            }
            dedupedMissing.push(field);
          });
          missingFieldsRef.current = dedupedMissing;
          pendingStartFieldRef.current = dedupedMissing[0] ?? null;
          setIsNarrationFlow(false);
          setNarrationStage("interactive");
          await new Promise<void>((resolve) => {
            let hasStarted = false;
            let isResolved = false;
            const resolveOnce = () => {
              if (isResolved) return;
              isResolved = true;
              resolve();
            };
            speechUtilityInstance.textToSpeech(
              "I did not hear a narration. I will now switch to guided questions for the form.",
              (isReading) => {
                if (isReading) {
                  hasStarted = true;
                  return;
                }
                if (hasStarted) resolveOnce();
              }
            );
          });
          runSequence().catch(() => undefined);
          return;
        }
        setNarrationStage("idle");
        setIsNarrationFlow(false);
        setIsRunning(false);
        onToggle?.(false);
        return;
      }
      const fields = getFields();
      await ensureSpokenLabels(fields);
      const fieldMeta = fields.map(collectFieldMeta);
      const parser = parseTranscript ?? defaultParseTranscript;
      setNarrationStage("analyzing");
      const parsed = await parser({ transcript: finalTranscript, fields: fieldMeta });
      setNarrationStage("filling");
      parsed.forEach((payload) => {
        const target = findFieldForPayload(fields, payload);
        if (target) {
          applyValueToField(target, payload.value);
        }
      });
      const missingFields = fields.filter((field) => isFieldMissing(field));
      if (missingFields.length) {
        if (fields.length <= 1) {
          setNarrationStage("idle");
          setIsNarrationFlow(false);
          setIsRunning(false);
          onToggle?.(false);
          return;
        }
        const dedupedMissing: HTMLElement[] = [];
        const handledGroups = new Set<HTMLElement>();
        missingFields.forEach((field) => {
          if (!isFocusableField(field)) return;
          if (field instanceof HTMLInputElement && field.type?.toLowerCase() === "checkbox") {
            const container = getCheckboxGroupContainer(field);
            if (container && handledGroups.has(container)) return;
            if (container) handledGroups.add(container);
          }
          dedupedMissing.push(field);
        });
        missingFieldsRef.current = dedupedMissing;
        pendingStartFieldRef.current = dedupedMissing[0] ?? null;
        setIsNarrationFlow(false);
        setNarrationStage("interactive");
        await new Promise<void>((resolve) => {
          let hasStarted = false;
          let isResolved = false;
          const resolveOnce = () => {
            if (isResolved) return;
            isResolved = true;
            resolve();
          };
          speechUtilityInstance.textToSpeech(
            "I will now switch to guided questions for any missing fields.",
            (isReading) => {
              if (isReading) {
                hasStarted = true;
                return;
              }
              if (hasStarted) resolveOnce();
            }
          );
        });
        runSequence().catch(() => undefined);
        return;
      }
      setNarrationStage("idle");
      setIsNarrationFlow(false);
      setIsRunning(false);
      onComplete?.();
      onToggle?.(false);
    } catch (error) {
      const nextError = new Error(getErrorMessage(error));
      setErrorMessage(nextError.message);
      onError?.(nextError);
      stopVoiceMode();
      onToggle?.(false);
    } finally {
      setIsProcessing(false);
    }
  };

  const runSequence = async () => {
    if (disabled) return;
    let fields = missingFieldsRef.current ?? getFields();
    if (!fields.length) return;
    void ensureSpokenLabels(fields);
    askedFieldKeysRef.current.clear();
    const activeElement =
      pendingStartFieldRef.current ?? (document.activeElement as HTMLElement | null);
    let startIndex = activeElement ? fields.indexOf(activeElement) : 0;
    let index = startIndex >= 0 ? startIndex : 0;
    const runId = runIdRef.current;
    setIsRunning(true);
    pendingStartFieldRef.current = null;

    const handledCheckboxGroups = new Set<HTMLElement>();
    while (index < fields.length) {
      if (cancelRef.current || runIdRef.current !== runId) break;
      const field = fields[index];
      if (field instanceof HTMLInputElement && field.type?.toLowerCase() === "checkbox") {
        const container = getCheckboxGroupContainer(field);
        if (container && handledCheckboxGroups.has(container)) {
          index += 1;
          continue;
        }
        if (container) handledCheckboxGroups.add(container);
      }
      const fieldKey = getFieldKey(field);
      if (fieldKey && askedFieldKeysRef.current.has(fieldKey)) {
        index += 1;
        continue;
      }
      if (!isFieldMissing(field)) {
        index += 1;
        continue;
      }
      if (fieldKey) askedFieldKeysRef.current.add(fieldKey);
      await captureField(field, runId);
      if (cancelRef.current || runIdRef.current !== runId) break;
      const pendingField = pendingStartFieldRef.current;
      if (pendingField) {
        fields = missingFieldsRef.current ?? getFields();
        const nextIndex = fields.indexOf(pendingField);
        pendingStartFieldRef.current = null;
        if (nextIndex >= 0) {
          index = nextIndex;
          continue;
        }
      }
      const nextField = fields[index + 1];
      if (nextField) {
        autoFocusRef.current = true;
        nextField.focus();
      }
      index += 1;
    }

    clearHighlight();
    missingFieldsRef.current = null;
    setIsRunning(false);
    setIsProcessing(false);

    if (!cancelRef.current && !isNarrationFlow) {
      onComplete?.();
      onToggle?.(false);
    }
  };

  const stopVoiceMode = () => {
    cancelRef.current = true;
    runIdRef.current += 1;
    SpeechUtility.stopRecognition();
    SpeechUtility.stopSpeech();
    SpeechUtility.destroyInstance();
    clearHighlight();
    setIsRunning(false);
    setIsProcessing(false);
    setIsNarrationFlow(false);
    setNarrationStage("idle");
    setNarrationTranscript("");
    setNarrationPartial("");
    onNarrationPartial?.("");
    onExit?.();
  };

  useEffect(() => {
    if (!enabled) {
      if (isRunning) stopVoiceMode();
      return;
    }
    if (spokenLabelMapRef.current.size === 0) {
      const fields = getFields();
      if (fields.length) {
        void ensureSpokenLabels(fields);
      }
    }
    setErrorMessage(null);
    cancelRef.current = false;
    runIdRef.current += 1;
    const run = narrationMode ? runNarrationFlow : runSequence;
    run().catch((error) => {
      const nextError = new Error(getErrorMessage(error));
      setErrorMessage(nextError.message);
      onError?.(nextError);
      stopVoiceMode();
      onToggle?.(false);
    });
  }, [enabled, narrationMode, parseTranscript]);

  useEffect(() => {
    onLanguageChangeRef.current = onLanguageChange;
  }, [onLanguageChange]);

  useEffect(() => {
    const label =
      SpeechUtility.translateLanguages[selectedLanguage] ??
      SpeechUtility.languages[selectedLanguage];
    onLanguageChangeRef.current?.(selectedLanguage, label);
  }, [selectedLanguage]);

  useEffect(() => {
    if (!enabled) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        stopVoiceMode();
        onToggle?.(false);
      }
    };
    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (autoFocusRef.current) {
        autoFocusRef.current = false;
        return;
      }
      const fields = getFields();
      if (!fields.includes(target)) return;
      pendingStartFieldRef.current = target;
      if (isRunning) {
        pendingStartFieldRef.current = target;
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("focusin", handleFocusIn);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enabled, getFields, isRunning]);

  const currentStatus = isProcessing
    ? statusText?.processing ?? "Listening..."
    : isNarrationFlow
      ? "Processing narration..."
      : isRunning
      ? statusText?.listening ?? "Voice capture active"
      : statusText?.idle ?? "Voice capture idle";

  const showNarrationOverlayEnabled =
    showNarrationOverlay &&
    narrationMode &&
    (enabled || isNarrationFlow) &&
    narrationStage !== "interactive";
  const showInteractiveOverlay =
    !isNarrationFlow && isInteractiveListening && !!interactiveOverlayPos;

  return (
    <div className={className ?? ""}>
      {showInteractiveOverlay && interactiveOverlayPos && (
        <div
          className="absolute z-40 w-[280px] rounded-md border border-primary bg-background/90 p-3 shadow-lg backdrop-blur"
          style={{
            top: interactiveOverlayPos.top,
            left: interactiveOverlayPos.left,
            transform:
              interactiveOverlayPos.placement === "above" ? "translateY(-100%)" : "none",
          }}
        >
          <div className="text-xs font-semibold text-default-700">
            Listening... {Math.ceil(interactiveElapsedMs / 1000)}s
          </div>
            <div className="mt-1 text-[11px] text-default-500">
              {isSystemSpeaking
                ? "Speaking prompt..."
                : interactiveRemainingMs > 0
                  ? `Auto-stops in ${Math.ceil(interactiveRemainingMs / 1000)}s of silence`
                  : "Waiting for speech..."}
            </div>
          <div className="mt-1 text-sm text-default-700">
            {interactivePartial || "Go ahead, I'm listening."}
          </div>
        </div>
      )}
      {showNarrationOverlayEnabled && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-xl rounded-lg border border-primary bg-content1 p-4 shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-foreground">Voice Narration</div>
                <div className="text-xs text-default-500">
                  {narrationStage === "welcome" && "Getting ready..."}
                  {narrationStage === "listening" && "Listening to your narration"}
                  {narrationStage === "analyzing" && "Analyzing transcript"}
                  {narrationStage === "filling" && "Filling fields"}
                  {narrationStage === "interactive" && "Switching to guided questions"}
                  {narrationStage === "idle" && "Idle"}
                </div>
              </div>
              <div className="text-xs text-default-500">{currentStatus}</div>
            </div>
            <div className="mt-3 rounded-md border border-default-200 bg-content2 p-3 text-sm text-default-700">
              <div
                className="whitespace-pre-wrap text-sm text-default-700"
                style={{
                  minHeight: "4.5em",
                  maxHeight: "15em",
                  overflowY: "auto",
                }}
              >
                {narrationPartial || narrationTranscript || "Start speaking to fill the form..."}
              </div>
            </div>
            {narrationPartial && narrationTranscript && (
              <div className="mt-2 text-xs text-default-500">
                Final: {narrationTranscript}
              </div>
            )}
            <div className="mt-3 flex items-center justify-between text-xs text-default-500">
              <span>Press Escape to stop</span>
              <button
                type="button"
                className="rounded-md border border-danger bg-danger/10 px-2 py-1 text-xs text-danger hover:bg-danger/20"
                onClick={() => {
                  stopVoiceMode();
                  onToggle?.(false);
                }}
              >
                Stop voice mode
              </button>
            </div>
            {errorMessage && (
              <div className="mt-2 text-xs text-danger">{errorMessage}</div>
            )}
          </div>
        </div>
      )}
      {showControls && (
        <div
          className={
            "flex flex-wrap items-center justify-between gap-2 rounded-md border border-primary bg-primary/10 px-3 py-2 text-sm text-primary " +
            controlsClassName
          }
        >
          <div className="flex flex-col gap-1">
            <span>{currentStatus}</span>
            {errorMessage && (
              <span className="text-xs text-danger">{errorMessage}</span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {showLanguageSelector && (
                <SearchableDropdown
                  selectedKey={selectedLanguage}
                  onSelectionChange={(key) => {
                    setSelectedLanguage(key);
                    SpeechUtility.setLanguage(key);
                    if (enabled && onToggle) {
                      SpeechUtility.destroyInstance();
                      onToggle(false);
                      window.setTimeout(() => onToggle(true), 0);
                    }
                  }}
                  availableLanguages={SpeechUtility.translateLanguages}
                  className="max-h-[200px] overflow-y-auto"
                  isDisabled={enabled}
                />
            )}
            {errorMessage && onToggle && (
              <CtyButton
                size="sm"
                variant="light"
                color="warning"
                onPress={() => {
                  setErrorMessage(null);
                  SpeechUtility.destroyInstance();
                  SpeechUtility.setLanguage(selectedLanguage);
                  onToggle(false);
                  window.setTimeout(() => onToggle(true), 0);
                }}
                isDisabled={disabled}
              >
                Retry
              </CtyButton>
            )}
            {showToggleButton && onToggle && (
              <CtyButton
                size="sm"
                variant="flat"
                color={enabled ? "danger" : "primary"}
                onPress={() => {
                  if (!enabled) {
                    SpeechUtility.destroyInstance();
                    SpeechUtility.setLanguage(selectedLanguage);
                  }
                  onToggle(!enabled);
                }}
                isDisabled={disabled}
              >
                {enabled ? toggleLabel?.off ?? "Turn off voice capture" : toggleLabel?.on ?? "Turn on voice capture"}
              </CtyButton>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceFormAssistant;
export { VoiceFormAssistant };
